import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  Bell,
  Check,
  Download,
  Plus,
  RefreshCw,
  UploadCloud,
} from "lucide-react";
import { api, post, type Row } from "../api";
import { Badge, Button, Callout, Empty, Modal, Skeleton } from "../ui";
import { useFormValidation, type ValidationField } from "../form-validation";
import { FieldError, ValidationSummary } from "./ValidationSummary";
import { displayReturnId } from "../terminology";
import {
  assignmentDueError,
  assignmentDueInput,
  assignmentDueUpdate,
  canManageOperations,
  canWriteOperations,
  filterOperationsReturns,
  isExternalWorkflowState,
  operationError,
  operationalAgeDays,
  operationStates,
  workflowLabel,
  workflowStatusLabel,
} from "../professional-operations";
import type { OperationsQueue } from "../professional-operations";
import "./professional-operations.css";

type Option = { value: string; label: string };
type ActionField = {
  name: string;
  label: string;
  required?: boolean;
  type?: string;
  value?: string;
  options?: Option[];
  maxLength?: number;
  help?: string;
  validate?: (value: string) => string | undefined;
};
type Action = {
  title: string;
  description?: string;
  fields: ActionField[];
  confirm?: string;
  submit: (values: Record<string, string>) => Promise<unknown>;
};
const date = (value: unknown) =>
  typeof value === "string" && Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString()
    : "Not recorded";
const priorityOptions = ["NORMAL", "HIGH", "URGENT"].map((value) => ({
  value,
  label: workflowLabel(value),
}));
const noteField: ActionField = {
  name: "notes",
  label: "Notes",
  type: "textarea",
  required: true,
  maxLength: 4000,
};
const due = (value: string) =>
  value ? new Date(value).toISOString() : undefined;
const optionsOf = (records: Row[], id: string, name = "name"): Option[] =>
  records.map((record) => ({
    value: String(record[id]),
    label: String(record[name] || "Unnamed record"),
  }));
const returnName = (record: Row) =>
  record.name || (record.reference ? displayReturnId(record) : "Return");

export function OperationForm({
  action,
  close,
  completed,
}: {
  action: Action;
  close: () => void;
  completed: (result: unknown) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      action.fields.map((field) => [field.name, field.value || ""]),
    ),
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const pending = useRef(false);
  // Modal owns a focus lifecycle keyed by this callback. Keep its identity
  // stable while editing, without allowing dismissal during a pending save.
  const dismiss = useCallback(() => {
    if (!pending.current) close();
  }, [close]);
  const fields: ValidationField[] = action.fields.map((field) => ({
    id: `operation-${field.name}`,
    label: field.label,
    value: values[field.name],
    required: field.required,
    maxLength: field.maxLength ?? (field.type === "textarea" ? 4000 : 254),
    validate: (value) => {
      if (!value) return undefined;
      if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/u.test(String(value)))
        return "Remove unsupported control characters from this entry.";
      if (
        field.type === "email" &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))
      )
        return "Enter a complete email address.";
      if (
        field.type === "datetime-local" &&
        !Number.isFinite(Date.parse(String(value)))
      )
        return "Enter a valid due date and time.";
      if (
        field.options &&
        !field.options.some((option) => option.value === value)
      )
        return "Choose an available option.";
      return field.validate?.(String(value));
    },
  }));
  const validation = useFormValidation(action.title, fields);
  async function save() {
    if (pending.current || !validation.validate()) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await action.submit(values);
      completed(result);
    } catch (failure) {
      setError(`${action.title}: ${operationError(failure)}`);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <Modal title={action.title} close={dismiss}>
      <div className="zt-operations">
        {action.description && <p>{action.description}</p>}
        <ValidationSummary issues={validation.issues} />
        {error && (
          <p role="alert" className="zt-operation-error">
            {error}
          </p>
        )}
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          {action.fields.map((field) => {
            const props = {
              ...validation.fieldProps(`operation-${field.name}`),
              value: values[field.name],
              disabled: busy,
              onChange: (
                event: React.ChangeEvent<
                  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                >,
              ) =>
                setValues((current) => ({
                  ...current,
                  [field.name]: event.target.value,
                })),
            };
            return (
              <label
                className="zt-field"
                key={field.name}
                htmlFor={`operation-${field.name}`}
              >
                {field.label}
                {field.required && (
                  <span className="zt-required"> (required)</span>
                )}
                {field.options ? (
                  <select {...props}>
                    <option value="">Select…</option>
                    {field.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "textarea" ? (
                  <textarea
                    {...props}
                    rows={4}
                    maxLength={field.maxLength ?? 4000}
                  />
                ) : (
                  <input
                    {...props}
                    type={field.type || "text"}
                    autoComplete={
                      field.type === "password" ? "current-password" : "off"
                    }
                    maxLength={field.maxLength ?? 254}
                  />
                )}
                {field.help && <small>{field.help}</small>}
                <FieldError
                  issue={validation.issue(`operation-${field.name}`)}
                />
              </label>
            );
          })}
          <div className="zt-operation-actions">
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? "Saving…" : action.confirm || "Save"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export function ProfessionalOperations({
  enabled,
  role,
  sessionScope,
  navigate,
  mfaRequired = false,
  mfaEnabled,
}: {
  enabled: boolean;
  role: string;
  sessionScope: string;
  navigate: (path: string) => void;
  mfaRequired?: boolean;
  mfaEnabled?: boolean;
}) {
  const [data, setData] = useState<Row | null>(null),
    [detail, setDetail] = useState<Row | null>(null);
  const [selected, setSelected] = useState(""),
    [tab, setTab] = useState("returns"),
    [queue, setQueue] = useState<OperationsQueue>("all"),
    [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [action, setAction] = useState<Action | null>(null),
    [invitation, setInvitation] = useState("");
  const [invitationRecord, setInvitationRecord] = useState<Row | null>(null);
  const generation = useRef(0),
    mounted = useRef(true);
  const portal = role === "taxpayer",
    owner = canManageOperations(role),
    staff = canWriteOperations(role);
  useEffect(
    () => () => {
      mounted.current = false;
      generation.current++;
    },
    [],
  );
  const load = useCallback(
    async (returnId = "") => {
      if (!enabled || (portal && mfaRequired)) return;
      const request = ++generation.current;
      setBusy(true);
      setError("");
      setDetail(null);
      try {
        const [next, item] = await Promise.all([
          api<Row>("/operations/bootstrap"),
          returnId
            ? api<Row>(`/operations/returns/${encodeURIComponent(returnId)}`)
            : Promise.resolve(null),
        ]);
        if (request !== generation.current || !mounted.current) return;
        setData(next);
        setDetail(item);
      } catch (failure) {
        if (request === generation.current && mounted.current) {
          setError(operationError(failure));
          setData(null);
          setDetail(null);
        }
      } finally {
        if (request === generation.current && mounted.current) setBusy(false);
      }
    },
    [enabled, sessionScope, portal, mfaRequired],
  );
  useEffect(() => {
    mounted.current = true;
    setData(null);
    setDetail(null);
    setSelected("");
    setAction(null);
    setInvitation("");
    setInvitationRecord(null);
    void load();
  }, [load]);
  const open = (id: string) => {
    setSelected(id);
    setNotice("");
    setInvitation("");
    void load(id);
  };
  const complete = (result: unknown) => {
    setAction(null);
    setNotice("Your change was saved. Refreshing the latest information…");
    const response = result as Row;
    if (response?.token) {
      setInvitation(
        `${location.origin}/portal/invitation#token=${encodeURIComponent(response.token)}`,
      );
      setInvitationRecord({ id: response.id, clientId: response.clientId });
    }
    void load(selected);
  };
  const records = useMemo(
    () => filterOperationsReturns(data?.returns || [], queue, search),
    [data, queue, search],
  );
  if (portal && mfaRequired)
    return (
      <Callout title="Secure your client portal account">
        Set up your authenticator before opening returns, documents or messages.
        Your firm's data remains protected while setup is incomplete.
        <br />
        <Button variant="primary" onClick={() => navigate("/auth/mfa")}>
          Set up authenticator
        </Button>
      </Callout>
    );
  if (!enabled)
    return (
      <Callout title="Professional Operations is not enabled on this server">
        This workspace has not been connected to the qualified operations
        database. Existing returns are unchanged. An administrator must complete
        and verify the local database activation before client invitations and
        workflow actions are available.
      </Callout>
    );
  const modeMismatch = data && data.mode !== (portal ? "client" : "staff");
  if (modeMismatch)
    return (
      <Callout title="Your workspace access must be refreshed">
        The loaded workspace does not match your signed-in access. Sign out and
        back in. No operations data is displayed.
      </Callout>
    );
  const addClient = () =>
    setAction({
      title: "Add client",
      fields: [
        { name: "name", label: "Client name", required: true, maxLength: 200 },
        {
          name: "email",
          label: "Client email address",
          type: "email",
          required: true,
        },
        { name: "phone", label: "Phone number", maxLength: 40 },
      ],
      submit: (values) =>
        post("/operations/clients", {
          name: values.name,
          email: values.email,
          phone: values.phone || undefined,
        }),
    });
  const inviteClient = (client: Row) =>
    setAction({
      title: `Invite ${client.name}`,
      description:
        "A single-use link will be created for this client's linked returns. Email and SMS delivery are not connected. Share the link through your approved secure channel. Re-enter your credentials to authorize access.",
      fields: [
        { ...noteField, required: false },
        {
          name: "currentPassword",
          label: "Your current password",
          type: "password",
          required: true,
          maxLength: 256,
        },
        {
          name: "totp",
          label: "Authenticator code (if MFA is enabled)",
          type: "password",
          maxLength: 20,
        },
        {
          name: "recoveryCode",
          label: "Recovery code (only if not using an authenticator code)",
          type: "password",
          maxLength: 100,
        },
      ],
      confirm: "Create secure invitation",
      submit: async (values) => ({
        ...(await post<Row>(
          `/operations/clients/${encodeURIComponent(client.id)}/invitations`,
          {
            notes: values.notes || undefined,
            currentPassword: values.currentPassword,
            totp: values.totp || undefined,
            recoveryCode: values.recoveryCode || undefined,
          },
        )),
        clientId: client.id,
      }),
    });
  const linkClient = (client: Row) =>
    setAction({
      title: `Link a return to ${client.name}`,
      description:
        "Link only this client's own return. This link determines which return may be granted through an invitation.",
      fields: [
        {
          name: "returnId",
          label: "Return",
          required: true,
          options: (data?.returns || []).map((record: Row) => ({
            value: record.id,
            label: `${returnName(record)} · ${record.taxYear}`,
          })),
        },
      ],
      submit: (values) =>
        post(`/operations/clients/${encodeURIComponent(client.id)}/returns`, {
          returnId: values.returnId,
        }),
    });
  return (
    <section
      className="zt-operations"
      aria-label={portal ? "Client portal" : "Professional operations"}
    >
      <div className="zt-operation-heading">
        <div>
          <h2>
            {selected
              ? "Return workspace"
              : portal
                ? "Your firm, securely connected"
                : "Practice overview"}
          </h2>
          <p>
            {portal
              ? "Only records shared with your account are displayed."
              : "Live records within your firm and return assignments. No signing or filing integrations are enabled."}
          </p>
        </div>
        <Button disabled={busy} onClick={() => void load(selected)}>
          <RefreshCw size={15} />
          Refresh
        </Button>
      </div>
      {error && (
        <div className="zt-operation-error" role="alert">
          <p>{error}</p>
          {portal && mfaEnabled === false && (
            <Button onClick={() => navigate("/auth/mfa")}>
              Set up authenticator
            </Button>
          )}
        </div>
      )}
      {notice && (
        <p className="zt-operation-notice" role="status">
          {notice}
        </p>
      )}
      {invitation && (
        <section className="card zt-operation-panel">
          <h3>Invitation created — not emailed</h3>
          <p>
            Share this single-use link through your approved secure channel. It
            will not be saved in this browser.
          </p>
          <label className="zt-field" htmlFor="secure-invite-link">
            Secure invitation link
            <input
              id="secure-invite-link"
              readOnly
              value={invitation}
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
          <Button onClick={() => setInvitation("")}>Hide link</Button>
          {invitationRecord && (
            <Button
              onClick={() =>
                setAction({
                  title: "Revoke invitation",
                  description:
                    "The single-use invitation link will stop working. This does not delete an existing client account.",
                  fields: [noteField],
                  submit: async (values) => {
                    const result = await post(
                      `/operations/clients/${encodeURIComponent(invitationRecord.clientId)}/invitations/${encodeURIComponent(invitationRecord.id)}/revoke`,
                      { notes: values.notes },
                    );
                    setInvitation("");
                    setInvitationRecord(null);
                    return result;
                  },
                })
              }
            >
              Revoke invitation
            </Button>
          )}
        </section>
      )}
      {busy ? (
        <Skeleton />
      ) : data && !selected ? (
        <>
          <nav className="zt-operation-tabs" aria-label="Practice views">
            {(portal
              ? ["returns", "notifications"]
              : ["returns", "board", "clients", "reports", "notifications"]
            ).map((item) => (
              <button
                type="button"
                aria-current={tab === item ? "page" : undefined}
                className={tab === item ? "active" : ""}
                key={item}
                onClick={() => setTab(item)}
              >
                {workflowLabel(item)}
                {item === "notifications" &&
                  (data.notifications || []).some((n: Row) => !n.readAt) && (
                    <span
                      className="zt-unread-dot"
                      aria-label="Unread notifications"
                    />
                  )}
              </button>
            ))}
          </nav>
          {["returns", "board"].includes(tab) && (
            <>
              <div className="zt-operation-toolbar">
                <label className="zt-field" htmlFor="operations-search">
                  Search returns
                  <input
                    id="operations-search"
                    type="search"
                    placeholder="Client name, return or year"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                {!portal && (
                  <label className="zt-field" htmlFor="operations-queue">
                    Work queue
                    <select
                      id="operations-queue"
                      value={queue}
                      onChange={(event) =>
                        setQueue(event.target.value as OperationsQueue)
                      }
                    >
                      {[
                        ["all", "All returns"],
                        ["assignment", "Needs preparer assignment"],
                        ["review", "Reviewer queue"],
                        ["missing", "Missing documents"],
                        ["aging", "Active return aging"],
                      ].map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {!records.length ? (
                <Empty
                  title="No returns in this view"
                  description={
                    portal
                      ? "Your firm has not shared any matching returns with your account."
                      : "Change the filters or create a return in the existing Returns workspace."
                  }
                  action={
                    !portal && owner ? (
                      <Button onClick={() => navigate("/intake/new")}>
                        Open new return
                      </Button>
                    ) : undefined
                  }
                />
              ) : tab === "board" ? (
                <div className="zt-workflow-board">
                  {["NOT_STARTED", ...operationStates].map((state) => {
                    const grouped = records.filter(
                      (record) => (record.state || "NOT_STARTED") === state,
                    );
                    return grouped.length ? (
                      <section className="zt-board-column" key={state}>
                        <h3>
                          {state === "NOT_STARTED"
                            ? "Not started"
                            : workflowStatusLabel(state)}{" "}
                          <span>{grouped.length}</span>
                        </h3>
                        {grouped.map((record) => (
                          <button
                            className="zt-return-tile"
                            type="button"
                            key={record.id}
                            onClick={() => open(record.id)}
                          >
                            <strong>{returnName(record)}</strong>
                            <span>
                              {record.taxYear} ·{" "}
                              {workflowLabel(record.priority || "NORMAL")}
                            </span>
                            <small>
                              {Number(record.missingDocumentCount || 0)}{" "}
                              outstanding document requests
                            </small>
                          </button>
                        ))}
                      </section>
                    ) : null;
                  })}
                </div>
              ) : (
                <ReturnDirectory records={records} open={open} />
              )}
            </>
          )}
          {tab === "clients" && !portal && (
            <>
              <div className="zt-operation-heading">
                <h3>Client directory</h3>
                {owner && (
                  <Button variant="primary" onClick={addClient}>
                    <Plus size={15} />
                    Add client
                  </Button>
                )}
              </div>
              {!(data.clients || []).length ? (
                <Empty
                  title="No clients to display"
                  description="Firm administrators can add clients and link their returns before issuing an invitation."
                />
              ) : (
                <div className="zt-operation-table-wrap">
                  <table className="zt-operation-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Portal access</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.clients.map((client: Row) => (
                        <tr key={client.id}>
                          <td>{client.name}</td>
                          <td>{client.email}</td>
                          <td>{client.phone || "—"}</td>
                          <td>
                            {client.membershipId
                              ? "Account linked"
                              : "No account linked"}
                          </td>
                          <td>
                            {owner && (
                              <div className="zt-operation-actions">
                                <Button onClick={() => linkClient(client)}>
                                  Link return
                                </Button>
                                <Button
                                  disabled={Boolean(client.membershipId)}
                                  onClick={() => inviteClient(client)}
                                >
                                  Invite client
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {tab === "reports" && !portal && (
            <OperationsReports
              reports={data.reports || {}}
              members={data.members || []}
            />
          )}
          {tab === "notifications" && (
            <OperationNotifications
              records={data.notifications || []}
              openReturn={open}
              change={(item) =>
                setAction({
                  title: "Mark notification as read",
                  fields: [],
                  submit: () =>
                    post(
                      `/operations/notifications/${encodeURIComponent(item.id)}/read`,
                    ),
                })
              }
            />
          )}
        </>
      ) : detail && selected ? (
        <>
          <Button
            onClick={() => {
              setSelected("");
              setDetail(null);
              void load();
            }}
          >
            <ArrowLeft size={15} />
            Back to {portal ? "your returns" : "practice overview"}
          </Button>
          <ReturnOperations
            key={`${sessionScope}:${selected}`}
            detail={detail}
            members={data?.members || []}
            portal={portal}
            owner={owner}
            staff={staff}
            setAction={setAction}
            complete={complete}
            reload={() => load(selected)}
          />
        </>
      ) : null}
      {action && (
        <OperationForm
          action={action}
          close={() => setAction(null)}
          completed={complete}
        />
      )}
    </section>
  );
}

export function ReturnDirectory({
  records,
  open,
}: {
  records: Row[];
  open: (id: string) => void;
}) {
  return (
    <div className="zt-operation-table-wrap">
      <table className="zt-operation-table">
        <caption>Returns available to your account</caption>
        <thead>
          <tr>
            <th>Return</th>
            <th>Tax year</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Due</th>
            <th>Days in current status</th>
            <th>Missing documents</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.id}>
              <td>
                <strong>{returnName(record)}</strong>
              </td>
              <td>{record.taxYear}</td>
              <td>{workflowStatusLabel(record.state)}</td>
              <td>{workflowLabel(record.priority || "NORMAL")}</td>
              <td>{date(record.dueAt)}</td>
              <td>{operationalAgeDays(record.stateEnteredAt) ?? "—"}</td>
              <td>{Number(record.missingDocumentCount || 0)}</td>
              <td>
                <Button onClick={() => open(record.id)}>
                  Open
                  <ArrowUpRight size={14} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReturnOperations({
  detail,
  members,
  portal,
  owner,
  staff,
  setAction,
  complete,
  reload,
}: {
  detail: Row;
  members: Row[];
  portal: boolean;
  owner: boolean;
  staff: boolean;
  setAction: (action: Action) => void;
  complete: (result: unknown) => void;
  reload: () => Promise<void>;
}) {
  const record = detail.return,
    workflow = detail.workflow;
  const permissions = detail.permissions || {};
  const endpoint = `/operations/returns/${encodeURIComponent(record.id)}`;
  const [tab, setTab] = useState("overview"),
    [visibility, setVisibility] = useState(portal ? "CLIENT" : "INTERNAL");
  const [uploading, setUploading] = useState(false),
    [uploadError, setUploadError] = useState("");
  const uploadLock = useRef(false),
    fileRef = useRef<HTMLInputElement>(null);
  const visibleMessages = (detail.messages || []).filter(
    (message: Row) => !portal || message.visibility === "CLIENT",
  );
  const clientOptions = optionsOf(detail.clients || [], "id");
  const assignment = (kind: string) => {
    const previousDue = (detail.assignments || []).find(
      (item: Row) => item.kind === kind,
    )?.dueAt;
    setAction({
      title: `Assign ${kind.toLowerCase()}`,
      fields: [
        {
          name: "membershipId",
          label: kind === "PREPARER" ? "Preparer" : "Reviewer",
          required: true,
          options: members
            .filter((member) =>
              kind === "PREPARER"
                ? ["preparer", "owner", "workspace_admin"].includes(member.role)
                : ["reviewer", "owner", "workspace_admin"].includes(
                    member.role,
                  ),
            )
            .map((member) => ({
              value: member.membershipId,
              label: `${member.name} · ${workflowLabel(member.role)}`,
            })),
        },
        {
          name: "dueAt",
          label: "Due date and time",
          type: "datetime-local",
          value: assignmentDueInput(previousDue),
          help: "Leave unchanged or blank to keep the existing assignment deadline. Enter a different date to change it.",
          validate: assignmentDueError,
        },
        {
          name: "priority",
          label: "Priority",
          required: true,
          options: priorityOptions,
          value: workflow?.priority || "NORMAL",
        },
        noteField,
      ],
      submit: (values) =>
        post(`${endpoint}/assign`, {
          version: workflow.version,
          kind,
          membershipId: values.membershipId,
          dueAt: assignmentDueUpdate(values.dueAt, previousDue),
          priority: values.priority,
          notes: values.notes,
        }),
    });
  };
  const transition = (next: Row) =>
    setAction({
      title: `Move to ${workflowLabel(next.to)}`,
      description: next.externalTracking
        ? "This records a milestone completed outside ZentroTax. ZentroTax will not sign, transmit, file or verify an IRS response. Supply the external reference and supporting notes; the status is a staff attestation only."
        : "The server will verify your permission and the return's latest status before saving this transition.",
      fields: [
        {
          ...noteField,
          required:
            next.requiresNotes === true || next.externalTracking === true,
        },
        ...(next.externalTracking
          ? [
              {
                name: "reference",
                label: "External completion or acknowledgment reference",
                required: true,
                maxLength: 500,
              },
              {
                name: "attested",
                label: "External milestone confirmation",
                required: true,
                options: [
                  {
                    value: "true",
                    label: "I verified this milestone outside ZentroTax",
                  },
                ],
              },
            ]
          : []),
      ],
      submit: (values) =>
        post(`${endpoint}/transition`, {
          version: workflow.version,
          to: next.to,
          notes: values.notes || undefined,
          ...(next.externalTracking
            ? {
                externalTracking: {
                  attested: values.attested === "true",
                  reference: values.reference,
                },
              }
            : {}),
        }),
    });
  const requestDocuments = () =>
    setAction({
      title: "Request documents",
      fields: [
        {
          name: "clientId",
          label: "Client",
          required: true,
          options: clientOptions,
        },
        {
          name: "title",
          label: "Document request",
          required: true,
          maxLength: 200,
        },
        {
          name: "description",
          label: "What the client should provide",
          type: "textarea",
          required: true,
          maxLength: 4000,
        },
        { name: "dueAt", label: "Due date and time", type: "datetime-local" },
      ],
      submit: (values) =>
        post(`${endpoint}/requests`, { ...values, dueAt: due(values.dueAt) }),
    });
  const respond = (request: Row) =>
    setAction({
      title: `Respond to ${request.title}`,
      description:
        "Upload any requested documents first. Explain what you provided or ask your firm for clarification.",
      fields: [
        {
          name: "message",
          label: "Response",
          type: "textarea",
          required: true,
          maxLength: 4000,
        },
      ],
      submit: (values) =>
        post(`${endpoint}/requests/${encodeURIComponent(request.id)}/respond`, {
          version: request.version,
          message: values.message,
        }),
    });
  const closeRequest = (request: Row) =>
    setAction({
      title: "Review document request",
      fields: [
        {
          name: "status",
          label: "Disposition",
          required: true,
          options: [
            { value: "FULFILLED", label: "Fulfilled — documents reviewed" },
            { value: "CANCELLED", label: "Cancelled — no longer needed" },
          ],
        },
        noteField,
      ],
      submit: (values) =>
        post(`${endpoint}/requests/${encodeURIComponent(request.id)}/close`, {
          version: request.version,
          status: values.status,
          notes: values.notes,
        }),
    });
  const compose = () =>
    setAction({
      title:
        visibility === "CLIENT"
          ? "Message client and firm"
          : visibility === "REVIEWER"
            ? "Add reviewer note"
            : "Add internal staff note",
      description:
        visibility === "CLIENT"
          ? "This message will be visible to the linked client and authorized firm staff."
          : "This note is not visible in the client portal.",
      fields: [
        ...(visibility === "CLIENT" && (!portal || clientOptions.length !== 1)
          ? [
              {
                name: "clientId",
                label: "Client",
                required: true,
                options: clientOptions,
              },
            ]
          : []),
        {
          name: "body",
          label: "Message",
          required: true,
          type: "textarea",
          maxLength: 8000,
        },
      ],
      submit: (values) =>
        post(`${endpoint}/messages`, {
          visibility,
          body: values.body,
          ...(visibility === "CLIENT"
            ? {
                clientId:
                  portal && clientOptions.length === 1
                    ? clientOptions[0].value
                    : values.clientId,
              }
            : {}),
        }),
    });
  async function upload(files: FileList | null) {
    if (!files?.length || uploadLock.current) return;
    const file = files[0];
    if (file.size > 5 * 1024 * 1024 || file.size === 0) {
      setUploadError(
        "Documents — Upload: choose a nonempty file no larger than 5 MB.",
      );
      return;
    }
    if (
      !["application/pdf", "image/png", "image/jpeg", "text/plain"].includes(
        file.type,
      )
    ) {
      setUploadError(
        "Documents — Upload: use a PDF, PNG, JPEG or plain-text document.",
      );
      return;
    }
    uploadLock.current = true;
    setUploading(true);
    setUploadError("");
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(new Error("File read failed"));
        reader.readAsDataURL(file);
      });
      await post(`/cases/${encodeURIComponent(record.id)}/evidence`, {
        name: file.name,
        mimeType: file.type,
        base64,
      });
      if (fileRef.current) fileRef.current.value = "";
      complete({});
    } catch (failure) {
      setUploadError(operationError(failure));
    } finally {
      uploadLock.current = false;
      setUploading(false);
    }
  }
  return (
    <>
      <div className="zt-operation-heading">
        <div>
          <h2>{returnName(record)}</h2>
          <p>Tax year {record.taxYear}</p>
        </div>
        <Badge>{workflowStatusLabel(workflow?.state)}</Badge>
      </div>
      {isExternalWorkflowState(workflow?.state) && (
        <Callout title="Externally tracked milestone">
          This status was recorded by firm staff. ZentroTax has not performed
          signing or filing and has not independently verified IRS acceptance.
        </Callout>
      )}
      <nav className="zt-operation-tabs" aria-label="Return operations">
        {(portal
          ? ["overview", "requests", "messages", "documents"]
          : ["overview", "requests", "messages", "documents", "activity"]
        ).map((item) => (
          <button
            type="button"
            key={item}
            aria-current={tab === item ? "page" : undefined}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {workflowLabel(item)}
          </button>
        ))}
      </nav>
      {tab === "overview" && (
        <section className="card zt-operation-panel">
          <h3>Workflow and responsibilities</h3>
          <dl className="zt-operation-facts">
            <div>
              <dt>Status</dt>
              <dd>{workflowStatusLabel(workflow?.state)}</dd>
            </div>
            <div>
              <dt>Priority</dt>
              <dd>{workflowLabel(workflow?.priority || "NORMAL")}</dd>
            </div>
            <div>
              <dt>Due</dt>
              <dd>{date(workflow?.dueAt)}</dd>
            </div>
          </dl>
          {!workflow && !portal && permissions.start === true && (
            <Button
              variant="primary"
              onClick={() =>
                setAction({
                  title: "Start return workflow",
                  description:
                    "Create the operational tracking record for this existing return. This does not calculate tax or prepare a filing.",
                  fields: [{ ...noteField, required: false }],
                  submit: (values) =>
                    post(`${endpoint}/start`, {
                      notes: values.notes || undefined,
                    }),
                })
              }
            >
              Start workflow
            </Button>
          )}
          {!portal && workflow && (
            <>
              <h4>Available next steps</h4>
              <div className="zt-operation-actions">
                {(detail.transitions || []).map((next: Row) => (
                  <Button key={next.to} onClick={() => transition(next)}>
                    {workflowStatusLabel(next.to)}
                  </Button>
                ))}
                {!(detail.transitions || []).length && (
                  <p>
                    No status changes are currently available for your role and
                    this return.
                  </p>
                )}
              </div>
            </>
          )}
          {!portal && (
            <>
              <h4>Assigned team</h4>
              {!(detail.assignments || []).length ? (
                <p>No team assignments have been recorded.</p>
              ) : (
                <ul className="zt-operation-list">
                  {detail.assignments.map((item: Row) => (
                    <li key={item.id || `${item.kind}:${item.membershipId}`}>
                      <strong>{workflowLabel(item.kind)}</strong>
                      <span>
                        {item.memberName ||
                          members.find(
                            (member) =>
                              member.membershipId === item.membershipId,
                          )?.name ||
                          "Assigned staff member"}
                      </span>
                      <small>
                        Assigned {date(item.assignedAt || item.createdAt)} · Due{" "}
                        {date(item.dueAt)}
                        {item.notes ? ` · ${item.notes}` : ""}
                      </small>
                      {item.assignedByUserId && (
                        <small>
                          Assigned by{" "}
                          {members.find(
                            (member) => member.userId === item.assignedByUserId,
                          )?.name || "Authorized firm administrator"}
                        </small>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {!portal && permissions.manageClients === true && (
            <>
              <h4>Client portal access</h4>
              {!(detail.clients || []).length ? (
                <p>
                  No client is linked. Link the client from the Client directory
                  before issuing an invitation.
                </p>
              ) : (
                <ul className="zt-operation-list">
                  {detail.clients.map((client: Row) => (
                    <li key={client.id}>
                      <strong>{client.name}</strong>
                      <span>
                        {client.membershipId
                          ? "Account linked"
                          : "Invitation not yet accepted"}
                      </span>
                      <Button
                        onClick={() =>
                          setAction({
                            title: `Remove ${client.name}'s return access`,
                            description:
                              "This removes the client's access to this return, including its requests, messages and shared documents. Historical records are preserved.",
                            fields: [noteField],
                            submit: (values) =>
                              post(
                                `/operations/clients/${encodeURIComponent(client.id)}/returns/revoke`,
                                { returnId: record.id, notes: values.notes },
                              ),
                          })
                        }
                      >
                        Remove return access
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {permissions.assign === true && workflow && (
            <div className="zt-operation-actions">
              <Button onClick={() => assignment("PREPARER")}>
                Assign or reassign preparer
              </Button>
              <Button onClick={() => assignment("REVIEWER")}>
                Assign or reassign reviewer
              </Button>
            </div>
          )}
          {!portal && permissions.escalate === true && workflow && (
            <Button
              onClick={() =>
                setAction({
                  title: "Escalate return",
                  fields: [
                    {
                      name: "priority",
                      label: "Priority",
                      required: true,
                      options: priorityOptions.filter((option) =>
                        ["HIGH", "URGENT"].includes(option.value),
                      ),
                    },
                    noteField,
                  ],
                  submit: (values) =>
                    post(`${endpoint}/escalate`, {
                      version: workflow.version,
                      notes: values.notes,
                      priority: values.priority,
                    }),
                })
              }
            >
              Escalate
            </Button>
          )}
        </section>
      )}
      {tab === "requests" && (
        <section className="card zt-operation-panel">
          <div className="zt-operation-heading">
            <h3>Document requests</h3>
            {!portal && permissions.requestDocuments === true && workflow && (
              <Button variant="primary" onClick={requestDocuments}>
                Request documents
              </Button>
            )}
          </div>
          {!(detail.requests || []).length ? (
            <p>No document requests have been recorded.</p>
          ) : (
            <ul className="zt-operation-list">
              {detail.requests.map((request: Row) => (
                <li key={request.id}>
                  <div className="zt-operation-heading">
                    <strong>{request.title}</strong>
                    <Badge>{workflowLabel(request.status)}</Badge>
                  </div>
                  <p>{request.description}</p>
                  <small>Due {date(request.dueAt)}</small>
                  {request.response && (
                    <p>
                      Client response:{" "}
                      {typeof request.response === "string"
                        ? request.response
                        : request.response.message}
                    </p>
                  )}
                  {!["FULFILLED", "CANCELLED"].includes(request.status) && (
                    <div className="zt-operation-actions">
                      {portal ? (
                        <Button onClick={() => respond(request)}>
                          Respond
                        </Button>
                      ) : (
                        permissions.requestDocuments === true && (
                          <Button onClick={() => closeRequest(request)}>
                            Review request
                          </Button>
                        )
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {tab === "messages" && (
        <section className="card zt-operation-panel">
          <div className="zt-operation-heading">
            <h3>Secure messages and notes</h3>
            {(portal || permissions.sendInternal === true) && (
              <Button variant="primary" onClick={compose}>
                Write message
              </Button>
            )}
          </div>
          {!portal && permissions.sendInternal === true && (
            <label className="zt-field" htmlFor="message-visibility">
              New message visibility
              <select
                id="message-visibility"
                value={visibility}
                onChange={(event) => setVisibility(event.target.value)}
              >
                <option value="INTERNAL">Internal staff only</option>
                <option value="CLIENT">Client and firm</option>
                {permissions.sendReviewer === true && (
                  <option value="REVIEWER">Reviewer notes</option>
                )}
              </select>
            </label>
          )}
          {!visibleMessages.length ? (
            <p>No messages have been recorded.</p>
          ) : (
            <ul className="zt-operation-list">
              {visibleMessages.map((message: Row) => (
                <li key={message.id}>
                  <div className="zt-operation-heading">
                    <strong>
                      {message.authorName ||
                        members.find(
                          (member) => member.userId === message.authorUserId,
                        )?.name ||
                        (portal ? "Client or firm account" : "Account user")}
                    </strong>
                    <Badge>
                      {message.visibility === "CLIENT"
                        ? "Client and firm"
                        : message.visibility === "REVIEWER"
                          ? "Reviewer note"
                          : "Internal staff"}
                    </Badge>
                  </div>
                  <p className="zt-message-body">{message.body}</p>
                  <small>{date(message.createdAt)}</small>
                  {message.read === true && <small>Read</small>}
                  {message.read !== true && (
                    <Button
                      onClick={() =>
                        setAction({
                          title: "Mark message as read",
                          fields: [],
                          submit: () =>
                            post(`${endpoint}/messages/read`, {
                              messageIds: [message.id],
                            }),
                        })
                      }
                    >
                      <Check size={14} />
                      Mark read
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {tab === "documents" && (
        <section className="card zt-operation-panel">
          <h3>Documents</h3>
          <p>
            Uploads use the existing encrypted document service and security
            scan. A successful upload does not mean the document is safe,
            reviewed or complete.
          </p>
          {permissions.uploadDocuments === true && (
            <label className="zt-field" htmlFor="operation-upload">
              <span>
                <UploadCloud size={17} />
                Upload document — PDF, PNG, JPEG or text, up to 5 MB
              </span>
              <input
                ref={fileRef}
                id="operation-upload"
                type="file"
                accept="application/pdf,image/png,image/jpeg,text/plain"
                disabled={uploading}
                aria-invalid={Boolean(uploadError)}
                aria-describedby={
                  uploadError ? "operation-upload-error" : undefined
                }
                onChange={(event) => void upload(event.target.files)}
              />
            </label>
          )}
          {uploading && <p role="status">Uploading securely…</p>}
          {uploadError && (
            <p
              id="operation-upload-error"
              className="zt-operation-error"
              role="alert"
            >
              {uploadError}
            </p>
          )}
          <h4>Uploaded documents</h4>
          {!(detail.documents || []).length ? (
            <p>No documents are available to your account.</p>
          ) : (
            <ul className="zt-operation-list">
              {detail.documents.map((document: Row) => (
                <li key={document.id}>
                  <strong>{document.name}</strong>
                  <Badge>{workflowLabel(document.scanState)}</Badge>
                  <small>
                    {document.downloadAvailable
                      ? "Security checks allow access"
                      : "Not available for download or sharing"}
                  </small>
                  {!portal && document.downloadAvailable === true && (
                    <a
                      className="btn"
                      href={`/api/cases/${encodeURIComponent(record.id)}/evidence/${encodeURIComponent(document.id)}/content`}
                      rel="noreferrer"
                    >
                      <Download size={15} />
                      Open document
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
          <h4>Completed returns shared by your firm</h4>
          {!(detail.artifacts || []).filter((item: Row) => !item.revokedAt)
            .length ? (
            <p>
              No completed return has been shared. This workspace does not
              generate or file a tax return.
            </p>
          ) : (
            <ul className="zt-operation-list">
              {detail.artifacts
                .filter((item: Row) => !item.revokedAt)
                .map((artifact: Row) => (
                  <li key={artifact.id}>
                    <strong>{artifact.title || "Completed return"}</strong>
                    <small>Shared {date(artifact.createdAt)}</small>
                    <a
                      className="btn"
                      href={`/api${endpoint}/artifacts/${encodeURIComponent(artifact.id)}/download`}
                      rel="noreferrer"
                    >
                      <Download size={15} />
                      Download
                    </a>
                    {permissions.shareArtifacts === true && (
                      <Button
                        onClick={() =>
                          setAction({
                            title: "Remove shared return access",
                            description:
                              "The underlying document remains subject to the firm's retention controls. This removes the portal download link.",
                            fields: [noteField],
                            submit: (values) =>
                              post(
                                `${endpoint}/artifacts/${encodeURIComponent(artifact.id)}/revoke`,
                                { notes: values.notes },
                              ),
                          })
                        }
                      >
                        Remove sharing
                      </Button>
                    )}
                  </li>
                ))}
            </ul>
          )}
          {!portal && permissions.shareArtifacts === true && (
            <ShareArtifact
              endpoint={endpoint}
              documents={detail.documents || []}
              clients={detail.clients || []}
              setAction={setAction}
            />
          )}
        </section>
      )}
      {tab === "activity" && !portal && (
        <section className="card zt-operation-panel">
          <h3>Return activity</h3>
          <p>
            Operational history for this return. Audit delivery and custody
            evidence remain in the administrator audit tools.
          </p>
          {!(detail.activity || []).length ? (
            <p>No workflow events have been recorded.</p>
          ) : (
            <ol className="zt-operation-list">
              {detail.activity.map((item: Row) => (
                <li key={item.id}>
                  <strong>{workflowLabel(item.kind)}</strong>
                  <span>
                    {item.actorName ||
                      members.find(
                        (member) => member.userId === item.actorUserId,
                      )?.name ||
                      "Recorded account user"}{" "}
                    · {date(item.createdAt)}
                  </span>
                  {item.priorState && (
                    <p>
                      {workflowStatusLabel(item.priorState)} →{" "}
                      {workflowStatusLabel(item.newState)}
                    </p>
                  )}
                  {item.notes && <p>{item.notes}</p>}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </>
  );
}

function ShareArtifact({
  endpoint,
  documents,
  clients,
  setAction,
}: {
  endpoint: string;
  documents: Row[];
  clients: Row[];
  setAction: (action: Action) => void;
}) {
  const candidates = documents.filter(
    (document) => document.versionId && document.downloadAvailable === true,
  );
  return (
    <>
      <Button
        disabled={!candidates.length || !clients.length}
        onClick={() =>
          setAction({
            title: "Share completed return",
            description:
              "Choose only a completed return reviewed by your firm. The server will verify the client's return access, exact document version and security status. This does not certify, sign or file the return.",
            fields: [
              {
                name: "clientId",
                label: "Client who can download this return",
                required: true,
                options: optionsOf(clients, "id"),
              },
              {
                name: "documentId",
                label: "Completed return document",
                required: true,
                options: candidates.map((document) => ({
                  value: document.id,
                  label: document.name || "Document",
                })),
              },
              {
                name: "title",
                label: "Client-facing title",
                required: true,
                maxLength: 200,
              },
            ],
            submit: (values) => {
              const document = candidates.find(
                (candidate) => candidate.id === values.documentId,
              )!;
              return post(`${endpoint}/artifacts`, {
                clientId: values.clientId,
                documentId: document.id,
                documentVersionId: document.versionId,
                title: values.title,
              });
            },
          })
        }
      >
        Share a completed return
      </Button>
      {(!candidates.length || !clients.length) && (
        <p>
          To share a completed return, first link its client and upload a
          document that passes the firm's security checks.
        </p>
      )}
    </>
  );
}

export function OperationNotifications({
  records,
  change,
  openReturn,
}: {
  records: Row[];
  change: (record: Row) => void;
  openReturn?: (returnId: string) => void;
}) {
  return (
    <section className="card zt-operation-panel">
      <h3>
        <Bell size={18} />
        Operations notifications
      </h3>
      <p>
        In-app notifications only. Email and SMS delivery are not connected.
      </p>
      {!records.length ? (
        <p>No operations notifications have been recorded.</p>
      ) : (
        <ul className="zt-operation-list">
          {records.map((record) => (
            <li key={record.id}>
              <strong>
                {record.title || workflowLabel(record.kind || record.eventType)}
              </strong>
              <p>{record.body || record.message}</p>
              <small>{date(record.createdAt)}</small>
              {record.returnId && openReturn && (
                <Button onClick={() => openReturn(record.returnId)}>
                  Open return
                  <ArrowUpRight size={14} />
                </Button>
              )}
              {record.readAt ? (
                <span>Read</span>
              ) : (
                <Button onClick={() => change(record)}>Mark read</Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReportTable({
  title,
  rows,
  columns,
}: {
  title: string;
  rows: Row[];
  columns: { key: string; label: string; render?: (row: Row) => ReactNode }[];
}) {
  return (
    <section className="card zt-operation-panel">
      <h3>{title}</h3>
      {!rows.length ? (
        <p>No records in this report.</p>
      ) : (
        <div className="zt-operation-table-wrap">
          <table className="zt-operation-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={String(row.id || row.membershipId || row.state || index)}
                >
                  {columns.map((column) => (
                    <td key={column.key}>
                      {column.render
                        ? column.render(row)
                        : (row[column.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function OperationsReports({
  reports,
  members,
}: {
  reports: Row;
  members: Row[];
}) {
  const member = (row: Row) =>
    row.membershipId === "UNASSIGNED"
      ? "Not assigned"
      : row.name ||
        members.find((item) => item.membershipId === row.membershipId)?.name ||
        "Assigned staff member";
  const buckets = (value: unknown, key: string) =>
    value && typeof value === "object" && !Array.isArray(value)
      ? Object.entries(value).map(([id, count]) => ({ [key]: id, count }))
      : [];
  return (
    <div className="zt-operation-reports">
      <p>
        Reports reflect only the records authorized for this account. Empty
        results do not imply that the entire firm has no outstanding work.
      </p>
      <ReportTable
        title="Returns by status"
        rows={buckets(reports.byStatus, "state")}
        columns={[
          {
            key: "state",
            label: "Status",
            render: (row) => workflowStatusLabel(row.state),
          },
          { key: "count", label: "Returns" },
        ]}
      />
      <ReportTable
        title="Returns by preparer"
        rows={buckets(reports.byPreparer, "membershipId")}
        columns={[
          { key: "name", label: "Preparer", render: member },
          { key: "count", label: "Returns" },
        ]}
      />
      <ReportTable
        title="Returns by reviewer"
        rows={buckets(reports.byReviewer, "membershipId")}
        columns={[
          { key: "name", label: "Reviewer", render: member },
          { key: "count", label: "Returns" },
        ]}
      />
      <ReportTable
        title="Staff workload"
        rows={buckets(reports.workload, "membershipId")}
        columns={[
          { key: "name", label: "Staff member", render: member },
          { key: "count", label: "Active assignments" },
        ]}
      />
      <ReportTable
        title="Return aging"
        rows={reports.aging || []}
        columns={[
          { key: "name", label: "Return" },
          {
            key: "state",
            label: "Status",
            render: (row) => workflowStatusLabel(row.state),
          },
          { key: "ageDays", label: "Days in current status" },
          {
            key: "overdue",
            label: "Overdue",
            render: (row) =>
              row.overdue === true ? "Yes" : row.overdue === false ? "No" : "—",
          },
        ]}
      />
      <section className="card zt-operation-panel">
        <h3>Missing documents</h3>
        <p>
          <strong>{reports.missingDocumentCount ?? "Not available"}</strong>{" "}
          open document requests
        </p>
      </section>
      <section className="card zt-operation-panel">
        <h3>Client response time</h3>
        <p>
          <strong>
            {reports.clientResponseHours?.average ?? "Not available"}
          </strong>{" "}
          average hours
        </p>
        <p>
          {reports.clientResponseHours?.sampleCount ?? 0} recorded responses. No
          response time is invented when there are no responses.
        </p>
      </section>
      <section className="card zt-operation-panel">
        <h3>Workflow throughput</h3>
        <p>Last {reports.throughput?.windowDays ?? "—"} days</p>
        <dl className="zt-operation-facts">
          <div>
            <dt>Operational approvals</dt>
            <dd>{reports.throughput?.approved ?? "—"}</dd>
          </div>
          <div>
            <dt>Externally reported acceptance</dt>
            <dd>{reports.throughput?.externallyReportedAccepted ?? "—"}</dd>
          </div>
        </dl>
        <p>
          These are workflow milestones, not tax certifications or independently
          verified IRS responses.
        </p>
      </section>
    </div>
  );
}
